import React, { useState, useContext, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography,
  Paper,
  Grid,
  Checkbox,
  Button,
  FormControlLabel,
  CircularProgress,
  Card,
  CardContent,
  Icon,
  Box,
  TextField,
  InputAdornment,
  Divider,
  Chip,
} from '@material-ui/core';
import { Link, useHistory } from 'react-router-dom';
import gql from 'graphql-tag';
import { useQuery, useMutation, Query } from 'react-apollo';
import { GlobalContext } from '../../global-context';
import moment from 'moment';
import boothbymail from '@BoothByMail/boothbymail-sdk';
import ShippingLabel from '../reusable/ShippingLabel';
import Printable from '../reusable/Printable';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(12),
    backgroundColor: '#f8f9fa',
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
  title: {
    marginBottom: theme.spacing(3),
    fontWeight: 600,
    color: theme.palette.primary.dark,
    display: 'flex',
    alignItems: 'center',
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    paddingBottom: theme.spacing(1),
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    position: 'relative',
    display: 'inline-block',
    '&:after': {
      content: '""',
      position: 'absolute',
      bottom: -8,
      left: 0,
      width: 40,
      height: 3,
      backgroundColor: theme.palette.primary.main,
      borderRadius: 2,
    },
  },
  checklistItem: {
    margin: theme.spacing(1, 0),
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
  },
  checkboxRoot: {
    marginRight: theme.spacing(1),
    marginTop: '-2px',
  },
  checkboxLabel: {
    marginLeft: 0,
    width: '100%',
  },
  infoCard: {
    height: '100%',
    marginBottom: theme.spacing(2),
  },
  infoValue: {
    fontWeight: 500,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(4),
    position: 'fixed',
    bottom: theme.spacing(3),
    left: 0,
    right: 0,
    padding: theme.spacing(2),
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    zIndex: 1000,
    boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
  },
  cancelButton: {
    padding: theme.spacing(2, 6),
    fontSize: '1.25rem',
    fontWeight: 'bold',
    minWidth: '200px',
    height: '64px',
  },
  finishButton: {
    backgroundColor: theme.palette.success.main,
    color: 'white',
    padding: theme.spacing(2, 6),
    fontSize: '1.25rem',
    fontWeight: 'bold',
    minWidth: '200px',
    height: '64px',
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    },
    '&:disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
    },
  },
  startButton: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    padding: theme.spacing(2, 6),
    fontSize: '1.25rem',
    fontWeight: 'bold',
    minWidth: '200px',
    height: '64px',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  centeredContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  statusIcon: {
    fontSize: 64,
    marginBottom: theme.spacing(2),
  },
  messageText: {
    marginBottom: theme.spacing(3),
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  orderTile: {
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
    },
  },
  orderGrid: {
    marginBottom: theme.spacing(4),
  },
  searchBox: {
    marginBottom: theme.spacing(3),
  },
  orderStatus: {
    display: 'inline-block',
    padding: theme.spacing(0.5, 1),
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    color: 'white',
    margin: theme.spacing(1, 0),
  },
  statusNew: {
    backgroundColor: theme.palette.info.main,
  },
  statusSubmitted: {
    backgroundColor: theme.palette.primary.main,
  },
  statusIncoming: {
    backgroundColor: theme.palette.primary.dark,
  },
  statusPreparing: {
    backgroundColor: theme.palette.warning.main,
  },
  statusPending: {
    backgroundColor: theme.palette.secondary.main,
  },
  statusDefault: {
    backgroundColor: theme.palette.grey[600],
  },
  noResults: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  equipmentInfo: {
    backgroundColor: '#f0f8ff', // light blue background
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    border: '1px solid #b3e0ff',
    borderRadius: theme.shape.borderRadius,
  },
  equipmentIcon: {
    fontSize: '1.2rem',
    marginRight: theme.spacing(1),
  },
  equipmentTitle: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  equipmentChip: {
    margin: theme.spacing(0.5),
    fontWeight: 500,
  },
}));

const GET_BOOTH_AND_ORDERS_QUERY = gql`
  query getBoothAndOrdersForOutbound($boothId: bigint!) {
    booths_by_pk(id: $boothId) {
      id
      sequence
      key_number
      ipad_udid
      active
    }
    orders(
      where: {
        status: {
          _nin: ["Done", "Cancelled", "Canceled", "Packing", "Delivered", "Outbound", "Inbound", "Returned", "Checkin"]
        }
        active: { _eq: true }
      }
      order_by: { event_date: asc }
    ) {
      id
      status
      event_name
      event_date
      first_name
      last_name
      email
      shipping_address1
      shipping_city
      shipping_state
      shipping_zip
      ship_date
      arrival_date
      return_ship_date
      return_date
      custom_logo
      props
      print_count
      print_layout
      backdrop_color
      service_level
      booth_id
    }

    # Get orders in Preparing status that have this booth assigned
    in_progress_orders: orders(
      where: { status: { _eq: "Preparing" }, booth_id: { _eq: $boothId }, active: { _eq: true } }
      limit: 1
    ) {
      id
      status
      event_name
      event_date
      first_name
      last_name
      email
      shipping_address1
      shipping_city
      shipping_state
      shipping_zip
      ship_date
      arrival_date
      return_ship_date
      return_date
      custom_logo
      props
      print_count
      print_layout
      backdrop_color
      service_level
      booth_id
    }
  }
`;

// Separate query for booth equipment info
const GET_BOOTH_EQUIPMENT_INFO = gql`
  query getBoothEquipmentInfo($boothId: bigint!) {
    orders(
      where: { booth_id: { _eq: $boothId }, status: { _in: ["Done"] }, active: { _eq: true } }
      order_by: { id: desc }
      limit: 1
    ) {
      id
      status
      event_name
      event_date
      backdrop_color
      print_count
      props
    }
  }
`;

const START_OUTBOUND_MUTATION = gql`
  mutation startOutbound($orderId: bigint!, $boothId: bigint!) {
    update_orders_by_pk(pk_columns: { id: $orderId }, _set: { status: "Preparing", booth_id: $boothId }) {
      id
      status
      booth_id
    }
  }
`;

const COMPLETE_OUTBOUND_MUTATION = gql`
  mutation completeOutbound($orderId: bigint!, $boothId: bigint!) {
    update_orders_by_pk(pk_columns: { id: $orderId }, _set: { status: "Outbound", booth_id: $boothId }) {
      id
      status
      booth_id
    }
  }
`;

const UNDO_OUTBOUND_MUTATION = gql`
  mutation undoOutbound($orderId: bigint!) {
    update_orders_by_pk(pk_columns: { id: $orderId }, _set: { status: "Ready", booth_id: 0 }) {
      id
      status
      booth_id
    }
  }
`;

function BoothOutbound(props) {
  const classes = useStyles();
  const ctx = useContext(GlobalContext);
  const history = useHistory();
  const boothId = parseInt(props.match.params.id);

  // Add ref to track initialization
  const checklistInitialized = useRef(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [outboundInProgress, setOutboundInProgress] = useState(false);

  // Define a more comprehensive checklist type with all possible items
  const [checklist, setChecklist] = useState({
    // Core verification items
    verifyBooth: false,
    verifyOrder: false,

    // Print shipping labels
    printShippingLabels: false,

    // Equipment verification
    tapeZipTiesToReturnLabel: false,
    includeBackdropTripods: false,
    includeBackdropCrossbar: false,
    verifyIpadFullyCharged: false,
    includeChargingCables: false,
    includeCorrectBackdrop: false,
    insertReturnLabel: false,
    verifyProps: false,

    // iPad setup
    unplugAndTuckWhiteCord: false,
    turnOnIpadGoToAdmin: false,
    pullBoothConfig: false,
    verifyCorrectAlbum: false,
    verifyCustomLogo: false,

    // Print setup (conditional)
    disablePrintingForDigital: false,
    setupPrinterForPrinting: false,
    addPaperAndInk: false,
    addExtraInkPaper100: false,
    addExtraInkPaper200: false,
    prepareExtraShipment300: false,
    connectToWifi: false,
    selectPrinter: false,
    performTestPrint: false,
    packPrinter: false,

    // Final steps
    exitAdminMode: false,
    placeIpadInCase: false,
    insertInstructionSheet: false,
    closeAndSecureCase: false,

    // Legacy items kept for reference but will be replaced
    packBoothSafely: false,
    includeBackdrop: false,
    includeTripods: false,
    includeAccessories: false,
    printShippingLabel: false,
    attachTrackingNumber: false,
    shipmentReady: false,
    verifyBackdropColor: false,
    includeBasicPrintSupplies: false,
    include2PrintCartridges: false,
    include5PrintCartridges: false,
    createExtraShipment: false,
    prepare3ExtraSupplies: false,
  });

  const { loading, error, data, refetch } = useQuery(GET_BOOTH_AND_ORDERS_QUERY, {
    variables: { boothId },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  // Component mount effect to immediately check for paused processes
  useEffect(() => {
    // On component mount, immediately refetch to ensure we have the latest data
    console.log(`[BoothOutbound] Component mounted for booth #${boothId}, checking for paused outbound processes`);
    refetch();
  }, [boothId, refetch]);

  // Get booth and orders data when available
  const booth = data?.booths_by_pk;
  const orders = data?.orders || [];
  const inProgressOrders = data?.in_progress_orders || [];

  // Function to get the selected order details
  const getSelectedOrder = () => {
    return (
      orders.find(order => order.id === selectedOrderId) || inProgressOrders.find(order => order.id === selectedOrderId)
    );
  };

  const selectedOrder = getSelectedOrder();

  // Filter orders based on search query and exclude orders already assigned to different booths
  const filteredOrders = orders.filter(
    order =>
      (searchQuery === '' || order.id.toString().includes(searchQuery)) &&
      // Only include orders that either have no booth assigned or are assigned to this booth
      (!order.booth_id || order.booth_id === boothId)
  );

  // Auto-select in-progress order if one exists
  useEffect(() => {
    // If there's an order already in Preparing status with this booth assigned
    if (inProgressOrders.length > 0 && !outboundInProgress && !selectedOrderId) {
      const continueOrder = inProgressOrders[0];
      console.log(`[BoothOutbound] Auto-selecting paused order #${continueOrder.id} for booth #${boothId}`);
      setSelectedOrderId(continueOrder.id);
      setOutboundInProgress(true);

      // Show notification that we're continuing an existing process
      ctx.handleNotifications(true, 'info', `Continuing outbound process for Order #${continueOrder.id}`);
    } else if (inProgressOrders.length === 0) {
      console.log(`[BoothOutbound] No in-progress orders found for booth #${boothId}`);
    } else {
      console.log(
        `[BoothOutbound] Skipping auto-select: outboundInProgress=${outboundInProgress}, selectedOrderId=${selectedOrderId}, inProgressOrders.length=${inProgressOrders.length}`
      );
    }
  }, [inProgressOrders, outboundInProgress, selectedOrderId, ctx, boothId]);

  // Auto-refresh to ensure we catch any paused outbound processes
  useEffect(() => {
    // Set up an interval to periodically check for in-progress orders
    const checkInterval = setInterval(() => {
      // Only auto-refresh if we're on the order selection screen and not already processing
      if (!outboundInProgress && !selectedOrderId) {
        console.log(`[BoothOutbound] Auto-refreshing orders for booth #${boothId}`);
        refetch();
      }
    }, 10000); // Check every 10 seconds

    // Cleanup interval on component unmount
    return () => clearInterval(checkInterval);
  }, [outboundInProgress, selectedOrderId, refetch, boothId]);

  // Additional useEffect to handle when data becomes available but auto-select didn't trigger
  useEffect(() => {
    // This covers the case where the query completes after the component mounts
    // but the auto-select didn't run due to timing
    if (!loading && !outboundInProgress && !selectedOrderId && inProgressOrders.length > 0) {
      const continueOrder = inProgressOrders[0];
      console.log(`[BoothOutbound] Late auto-select triggered for order #${continueOrder.id} on booth #${boothId}`);
      setSelectedOrderId(continueOrder.id);
      setOutboundInProgress(true);
      ctx.handleNotifications(true, 'info', `Found paused outbound process for Order #${continueOrder.id}`);
    }
  }, [loading, outboundInProgress, selectedOrderId, inProgressOrders, ctx, boothId]);

  // Generate a unique storage key for this booth/order combination
  const storageKey = selectedOrderId ? `outbound_checklist_booth_${boothId}_order_${selectedOrderId}` : null;

  // Load saved checklist from localStorage when component mounts or order data becomes available
  useEffect(() => {
    // Only run this effect when we have a storageKey and outbound is in progress
    if (storageKey && outboundInProgress && !checklistInitialized.current) {
      try {
        const savedChecklist = localStorage.getItem(storageKey);
        if (savedChecklist) {
          const parsedChecklist = JSON.parse(savedChecklist);

          // Create a new merged checklist based on the current state
          const mergedChecklist = { ...checklist };

          // Only copy values for keys that exist in our checklist
          Object.keys(mergedChecklist).forEach(key => {
            if (typeof parsedChecklist[key] === 'boolean') {
              mergedChecklist[key] = parsedChecklist[key];
            }
          });

          setChecklist(mergedChecklist);
          checklistInitialized.current = true;
        }
      } catch (e) {
        console.error('Error parsing saved checklist:', e);
        localStorage.removeItem(storageKey);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, outboundInProgress]); // Intentionally remove checklist to avoid infinite loop

  // Set up initial dynamic checklist based on order details
  useEffect(() => {
    if (selectedOrder && outboundInProgress && !checklistInitialized.current) {
      // Mark as initialized to prevent further runs
      checklistInitialized.current = true;

      // Only set checklist once when the order is first selected
      if (!storageKey || !localStorage.getItem(storageKey)) {
        // Create a fresh checklist with all unchecked values
        setChecklist({
          // Core verification items
          verifyBooth: false,
          verifyOrder: false,

          // Print shipping labels
          printShippingLabels: false,

          // Equipment verification
          tapeZipTiesToReturnLabel: false,
          includeBackdropTripods: false,
          includeBackdropCrossbar: false,
          verifyIpadFullyCharged: false,
          includeChargingCables: false,
          includeCorrectBackdrop: false,
          insertReturnLabel: false,
          verifyProps: false,

          // iPad setup
          unplugAndTuckWhiteCord: false,
          turnOnIpadGoToAdmin: false,
          pullBoothConfig: false,
          verifyCorrectAlbum: false,
          verifyCustomLogo: false,

          // Print setup (conditional)
          disablePrintingForDigital: false,
          setupPrinterForPrinting: false,
          addPaperAndInk: false,
          addExtraInkPaper100: false,
          addExtraInkPaper200: false,
          prepareExtraShipment300: false,
          connectToWifi: false,
          selectPrinter: false,
          performTestPrint: false,
          packPrinter: false,

          // Final steps
          exitAdminMode: false,
          placeIpadInCase: false,
          insertInstructionSheet: false,
          closeAndSecureCase: false,

          // Legacy items that will be phased out
          packBoothSafely: false,
          includeBackdrop: false,
          includeTripods: false,
          includeAccessories: false,
          printShippingLabel: false,
          attachTrackingNumber: false,
          shipmentReady: false,
          verifyBackdropColor: false,
          includeBasicPrintSupplies: false,
          include2PrintCartridges: false,
          include5PrintCartridges: false,
          createExtraShipment: false,
          prepare3ExtraSupplies: false,
        });
      }
    }
  }, [selectedOrder, outboundInProgress, storageKey]);

  // Reset initialization flag when order changes
  useEffect(() => {
    checklistInitialized.current = false;
  }, [selectedOrderId]);

  const [startOutbound, { loading: startLoading }] = useMutation(START_OUTBOUND_MUTATION, {
    onCompleted: () => {
      ctx.handleNotifications(true, 'success', `Outbound process started for Order #${selectedOrderId}`);
      setOutboundInProgress(true);
      refetch();
    },
    onError: err => {
      ctx.handleNotifications(true, 'error', `Failed to start outbound process: ${err.message}`);
    },
  });

  const [completeOutbound, { loading: completeLoading }] = useMutation(COMPLETE_OUTBOUND_MUTATION, {
    onCompleted: () => {
      // Clear the saved checklist when outbound is complete
      if (storageKey) {
        localStorage.removeItem(storageKey);
      }

      // Add note about outbound completion
      try {
        // Add a note to the order
        boothbymail.orders.addNotes([
          {
            order_id: selectedOrderId,
            user_id: 0, // Use system user ID
            type: 'Note',
            body: `Outbound completed using booth id: ${boothId}`,
          },
        ]);

        // Also write to the logs for better tracking
        boothbymail.logs.writeLog(
          `Outbound process completed for order #${selectedOrderId} using booth id: ${boothId}`,
          'system',
          boothId,
          null,
          selectedOrderId
        );
      } catch (error) {
        console.error('Error adding outbound completion record:', error);
      }

      ctx.handleNotifications(
        true,
        'success',
        `Order #${selectedOrderId} has been successfully outbounded with Booth #${boothId}.`
      );
      history.push(`/booths/${boothId}`);
    },
    onError: err => {
      ctx.handleNotifications(true, 'error', `Failed to complete outbound process: ${err.message}`);
    },
  });

  const [undoOutbound, { loading: undoLoading }] = useMutation(UNDO_OUTBOUND_MUTATION, {
    onCompleted: () => {
      // Clear the saved checklist when outbound is undone
      if (storageKey) {
        localStorage.removeItem(storageKey);
      }

      // Add note about undoing outbound
      try {
        // Add a note to the order
        boothbymail.orders.addNotes([
          {
            order_id: selectedOrderId,
            user_id: 0, // Use system user ID
            type: 'Note',
            body: `Outbound process canceled and order reset to Ready status, booth assignment removed.`,
          },
        ]);

        // Also write to the logs for better tracking
        boothbymail.logs.writeLog(
          `Outbound process canceled for order #${selectedOrderId}, reset to Ready status, booth assignment removed from ${boothId}.`,
          'system',
          boothId,
          null,
          selectedOrderId
        );
      } catch (error) {
        console.error('Error adding undo outbound record:', error);
      }

      ctx.handleNotifications(
        true,
        'success',
        `Outbound process for Order #${selectedOrderId} has been canceled and reset to Ready status.`
      );
      history.push(`/booths/${boothId}`);
    },
    onError: err => {
      ctx.handleNotifications(true, 'error', `Failed to undo outbound process: ${err.message}`);
    },
  });

  const handleChecklistChange = event => {
    const newChecklist = {
      ...checklist,
      [event.target.name]: event.target.checked,
    };

    setChecklist(newChecklist);

    // Save checklist to localStorage whenever it changes
    if (storageKey && outboundInProgress) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(newChecklist));
      } catch (e) {
        console.error('Error saving checklist to localStorage:', e);
      }
    }
  };

  const handleStartOutbound = async orderId => {
    setSelectedOrderId(orderId);

    // Add note about outbound start
    try {
      // Add a note to the order
      await boothbymail.orders.addNotes([
        {
          order_id: orderId,
          user_id: 0, // Use system user ID
          type: 'Note',
          body: `Outbound started using booth id: ${boothId}`,
        },
      ]);

      // Also write to the logs for better tracking
      await boothbymail.logs.writeLog(
        `Outbound process started for order #${orderId} using booth id: ${boothId}`,
        'system',
        boothId,
        null,
        orderId
      );
    } catch (error) {
      console.error('Error adding outbound start record:', error);
    }

    startOutbound({
      variables: {
        orderId: orderId,
        boothId: boothId,
      },
    });
  };

  const handleFinishOutbound = async () => {
    completeOutbound({
      variables: {
        orderId: selectedOrderId,
        boothId: boothId,
      },
    });
  };

  const handleUndoOutbound = async () => {
    if (
      window.confirm(
        `Are you sure you want to cancel the outbound process for Order #${selectedOrderId}? This will reset the order status back to "Ready" and remove the booth assignment.`
      )
    ) {
      undoOutbound({
        variables: {
          orderId: selectedOrderId,
        },
      });
    }
  };

  const handlePauseOutbound = async () => {
    // Only try to log if there's a selected order
    if (!selectedOrderId) {
      history.push(`/booths/${boothId}`);
      return;
    }

    try {
      // Add a note to the order
      await boothbymail.orders.addNotes([
        {
          order_id: selectedOrderId,
          user_id: 0, // Use system user ID
          type: 'Note',
          body: `Outbound process paused for booth id: ${boothId}`,
        },
      ]);

      // Also write to the logs for better tracking
      await boothbymail.logs.writeLog(
        `Outbound process paused for order #${selectedOrderId} with booth id: ${boothId}`,
        'system',
        boothId,
        null,
        selectedOrderId
      );

      ctx.handleNotifications(
        true,
        'info',
        `Outbound process for Order #${selectedOrderId} has been paused. You can continue later.`
      );

      // Navigate back to the booth page
      history.push(`/booths/${boothId}`);
    } catch (error) {
      console.error('Error adding pause outbound record:', error);
      // Still navigate back even if logging fails
      history.push(`/booths/${boothId}`);
    }
  };

  // Check if all required tasks are completed
  const getRequiredTasksList = () => {
    const requiredTasks = [
      // Core verification
      'verifyOrder',

      // Print shipping labels
      'printShippingLabels',

      // Essential equipment
      'tapeZipTiesToReturnLabel',
      'includeBackdropTripods',
      'includeBackdropCrossbar',
      'verifyIpadFullyCharged',
      'includeChargingCables',
      'includeCorrectBackdrop',
      'verifyProps',

      // iPad setup
      'unplugAndTuckWhiteCord',
      'turnOnIpadGoToAdmin',
      'pullBoothConfig',
      'verifyCorrectAlbum',

      // Insert return label (moved after props)
      'insertReturnLabel',

      // Final steps always required
      'exitAdminMode',
      'placeIpadInCase',
      'insertInstructionSheet',
      'closeAndSecureCase',
    ];

    // Add conditional requirements
    if (selectedOrder) {
      // Logo verification
      if (selectedOrder.custom_logo) requiredTasks.push('verifyCustomLogo');

      // Digital vs Print booth
      if (selectedOrder.print_count === 0) {
        // Digital booth - must disable printing
        requiredTasks.push('disablePrintingForDigital');
      } else {
        // Print booth - printer setup required
        requiredTasks.push('setupPrinterForPrinting');
        requiredTasks.push('addPaperAndInk');
        requiredTasks.push('connectToWifi');
        requiredTasks.push('selectPrinter');
        requiredTasks.push('performTestPrint');
        requiredTasks.push('packPrinter');

        // Different paper/ink requirements based on print count
        if (selectedOrder.print_count === 100) {
          requiredTasks.push('addExtraInkPaper100');
        } else if (selectedOrder.print_count === 200) {
          requiredTasks.push('addExtraInkPaper200');
        } else if (selectedOrder.print_count >= 300) {
          requiredTasks.push('prepareExtraShipment300');
        }
      }
    }

    return requiredTasks;
  };

  const allTasksCompleted = selectedOrder ? getRequiredTasksList().every(task => checklist[task] === true) : false;

  if (loading) {
    return (
      <CircularProgress
        style={{
          display: 'block',
          margin: 'auto',
        }}
      />
    );
  }

  if (error) {
    return (
      <Paper className={classes.paper}>
        <Typography color='error'>Error loading booth details: {error.message}</Typography>
        <Button variant='contained' color='primary' component={Link} to={`/booths/${boothId}`}>
          Back to Booth
        </Button>
      </Paper>
    );
  }

  if (!booth) {
    return (
      <Paper className={classes.paper}>
        <Typography variant='h5'>Booth not found</Typography>
        <Button variant='contained' color='primary' component={Link} to='/booths' className={classes.buttonContainer}>
          Back to Booths
        </Button>
      </Paper>
    );
  }

  // Render order selection screen
  if (!outboundInProgress) {
    return (
      <div className={classes.root}>
        <Typography variant='h4' className={classes.title}>
          <Icon style={{ marginRight: '8px' }}>local_shipping</Icon>
          Select Order for Outbound with Booth #{booth.id}
        </Typography>

        {/* Equipment Info Card - Based on latest order for this booth */}
        <Query query={GET_BOOTH_EQUIPMENT_INFO} variables={{ boothId }} fetchPolicy='network-only'>
          {result => {
            const { loading: equipLoading, error: equipError, data: equipData } = result;

            if (equipLoading || equipError || !equipData) return null;

            const latestOrders = equipData.orders;
            if (!latestOrders || latestOrders.length === 0) return null;

            const latestOrder = latestOrders[0];

            return (
              <Paper className={`${classes.paper} ${classes.equipmentInfo}`}>
                <Typography className={classes.equipmentTitle}>
                  <Icon className={classes.equipmentIcon}>info</Icon>
                  Likely Equipment in Booth Case
                </Typography>
                <Typography variant='body2' gutterBottom>
                  Based on the latest order (#{latestOrder.id}) this booth was used for:
                </Typography>

                <Grid container spacing={2} style={{ marginTop: '8px' }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant='body2' style={{ fontWeight: 500 }}>
                      <Icon className={classes.equipmentIcon}>palette</Icon>
                      Backdrop:
                      <Chip
                        size='small'
                        className={classes.equipmentChip}
                        label={latestOrder.backdrop_color || 'Default'}
                        color={
                          latestOrder.backdrop_color && latestOrder.backdrop_color !== 'Default' ? 'primary' : 'default'
                        }
                      />
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant='body2' style={{ fontWeight: 500 }}>
                      <Icon className={classes.equipmentIcon}>print</Icon>
                      Printer:
                      <Chip
                        size='small'
                        className={classes.equipmentChip}
                        label={latestOrder.print_count > 0 ? 'Yes' : 'No'}
                        color={latestOrder.print_count > 0 ? 'primary' : 'default'}
                      />
                    </Typography>
                  </Grid>

                  {latestOrder.props && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant='body2' style={{ fontWeight: 500 }}>
                        <Icon className={classes.equipmentIcon}>emoji_objects</Icon>
                        Props:
                        <Chip
                          size='small'
                          className={classes.equipmentChip}
                          label={latestOrder.props || 'Default'}
                          color='primary'
                        />
                      </Typography>
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant='body2' style={{ fontWeight: 500 }}>
                      <Icon className={classes.equipmentIcon}>event</Icon>
                      Last Event:
                      <span style={{ marginLeft: '4px' }}>{latestOrder.event_name || 'Unknown'}</span>
                    </Typography>
                  </Grid>
                </Grid>

                <Typography variant='caption' color='textSecondary' style={{ marginTop: '8px', display: 'block' }}>
                  This information is based on the last order processed with this booth
                  {latestOrder.event_date
                    ? ` (event date: ${moment(latestOrder.event_date).format('MMM D, YYYY')})`
                    : ''}
                </Typography>
              </Paper>
            );
          }}
        </Query>

        <Paper className={classes.paper}>
          <Box className={classes.searchBox}>
            <TextField
              fullWidth
              variant='outlined'
              label='Search by Order ID'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Icon>search</Icon>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {filteredOrders.length > 0 ? (
            <Grid container spacing={3} className={classes.orderGrid}>
              {filteredOrders.map(order => (
                <Grid item xs={12} sm={6} md={4} key={order.id}>
                  <Card className={classes.orderTile} onClick={() => handleStartOutbound(order.id)} elevation={3}>
                    <CardContent>
                      <Typography variant='h5' gutterBottom>
                        Order #{order.id}
                      </Typography>

                      <div
                        className={`${classes.orderStatus} ${
                          classes[`status${order.status.replace(/\s+/g, '')}`] || classes.statusDefault
                        }`}
                      >
                        {order.status}
                        {order.booth_id ? ` (Booth: ${order.booth_id})` : ''}
                      </div>

                      <Typography variant='body1' style={{ marginTop: '8px', fontWeight: 'bold' }}>
                        {order.event_name}
                      </Typography>

                      <Typography variant='body2' color='textSecondary' style={{ marginTop: '8px' }}>
                        <span style={{ fontWeight: 'bold' }}>Event Date:</span>{' '}
                        {order.event_date ? moment(order.event_date).format('MM/DD/YYYY') : 'N/A'}
                      </Typography>

                      <Typography variant='body2' color='textSecondary'>
                        <span style={{ fontWeight: 'bold' }}>Customer:</span> {order.first_name} {order.last_name}
                      </Typography>

                      {/* Added information about print count and backdrop color */}
                      <Grid container spacing={1} style={{ marginTop: '8px' }}>
                        <Grid item xs={6}>
                          <Typography variant='body2' color='textSecondary'>
                            <span style={{ fontWeight: 'bold' }}>Prints:</span>{' '}
                            <Chip
                              size='small'
                              label={order.print_count || '0'}
                              color={order.print_count > 0 ? 'primary' : 'default'}
                              style={{ marginLeft: '4px' }}
                            />
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant='body2' color='textSecondary'>
                            <span style={{ fontWeight: 'bold' }}>Backdrop:</span>{' '}
                            <Chip
                              size='small'
                              label={order.backdrop_color || 'Default'}
                              color={order.backdrop_color && order.backdrop_color !== 'Default' ? 'primary' : 'default'}
                              style={{ marginLeft: '4px' }}
                            />
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <div className={classes.noResults}>
              <Icon style={{ fontSize: 48, color: '#ccc', marginBottom: 16 }}>inbox</Icon>
              <Typography variant='h6'>No orders available for outbound</Typography>
              <Typography variant='body2' color='textSecondary'>
                {searchQuery
                  ? 'Try a different search term or clear your filter'
                  : 'There are no orders that need to be outbounded at this time'}
              </Typography>
            </div>
          )}

          {inProgressOrders.length > 0 && (
            <Paper className={classes.paper} style={{ marginTop: '24px', backgroundColor: '#e8f4fd' }}>
              <Typography variant='h6' gutterBottom>
                <Icon style={{ verticalAlign: 'middle', marginRight: '8px' }}>info</Icon>
                Continue In-Progress Outbound
              </Typography>
              <Typography variant='body2' paragraph>
                There is an order already in the outbound process using this booth:
              </Typography>

              <Grid container spacing={2}>
                {inProgressOrders.map(order => (
                  <Grid item xs={12} key={order.id}>
                    <Card
                      className={classes.orderTile}
                      onClick={() => handleStartOutbound(order.id)}
                      style={{ backgroundColor: '#fff', border: '1px solid #2196f3' }}
                    >
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <Typography variant='body1' style={{ fontWeight: 'bold' }}>
                              Order #{order.id}
                            </Typography>
                            <div className={`${classes.orderStatus} ${classes.statusPreparing}`}>{order.status}</div>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography variant='body2'>
                              <span style={{ fontWeight: 'bold' }}>Event:</span> {order.event_name}
                            </Typography>
                            <Typography variant='body2'>
                              <span style={{ fontWeight: 'bold' }}>Date:</span>{' '}
                              {moment(order.event_date).format('MM/DD/YYYY')}
                            </Typography>

                            {/* Added information about print count and backdrop */}
                            <Grid container spacing={1} style={{ marginTop: '8px' }}>
                              <Grid item xs={6}>
                                <Typography variant='body2'>
                                  <span style={{ fontWeight: 'bold' }}>Prints:</span>{' '}
                                  <Chip
                                    size='small'
                                    label={order.print_count || '0'}
                                    color={order.print_count > 0 ? 'primary' : 'default'}
                                    style={{ marginLeft: '4px' }}
                                  />
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant='body2'>
                                  <span style={{ fontWeight: 'bold' }}>Backdrop:</span>{' '}
                                  <Chip
                                    size='small'
                                    label={order.backdrop_color || 'Default'}
                                    color={
                                      order.backdrop_color && order.backdrop_color !== 'Default' ? 'primary' : 'default'
                                    }
                                    style={{ marginLeft: '4px' }}
                                  />
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Button
                              variant='contained'
                              color='primary'
                              onClick={e => {
                                e.stopPropagation();
                                handleStartOutbound(order.id);
                              }}
                              fullWidth
                            >
                              Continue Outbound
                            </Button>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}

          {/* Debug information for troubleshooting - remove in production */}
          {process.env.NODE_ENV === 'development' && (
            <Paper className={classes.paper} style={{ marginTop: '16px', backgroundColor: '#fff3e0' }}>
              <Typography variant='body2' color='textSecondary'>
                <strong>Debug Info:</strong>
                <br />
                Loading: {loading.toString()}
                <br />
                Booth ID: {boothId}
                <br />
                In Progress Orders Count: {inProgressOrders.length}
                <br />
                In Progress Orders:{' '}
                {JSON.stringify(inProgressOrders.map(o => ({ id: o.id, status: o.status, booth_id: o.booth_id })))}
                <br />
                Outbound In Progress: {outboundInProgress.toString()}
                <br />
                Selected Order ID: {selectedOrderId || 'None'}
              </Typography>
            </Paper>
          )}
        </Paper>

        <div className={classes.buttonContainer}>
          <Button
            variant='outlined'
            color='default'
            component={Link}
            to={`/booths/${boothId}`}
            className={classes.cancelButton}
          >
            Back to Booth
          </Button>

          <Button
            variant='contained'
            color='primary'
            onClick={() => {
              // Force a fresh refetch to look for paused outbound processes
              refetch();
              ctx.handleNotifications(true, 'info', 'Refreshing order list...');
            }}
            startIcon={<Icon>refresh</Icon>}
            size='large'
          >
            {inProgressOrders.length > 0 ? 'Refresh Orders' : 'Check for Paused Outbounds'}
          </Button>
        </div>
      </div>
    );
  }

  // Render outbound process checklist
  return (
    <div className={classes.root}>
      <Typography variant='h4' className={classes.title}>
        <Icon style={{ marginRight: '8px' }}>local_shipping</Icon>
        {selectedOrder.booth_id === boothId
          ? `Continuing Outbound for Order #${selectedOrderId} with Booth #${booth.id}`
          : `Outbound Process for Order #${selectedOrderId} with Booth #${booth.id}`}
      </Typography>

      {selectedOrder && (
        <Paper className={classes.paper} style={{ marginBottom: '16px' }}>
          <Grid container spacing={2}>
            {/* First row */}
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant='body2' color='textSecondary' style={{ fontWeight: 'bold' }}>
                Order ID:
                <span style={{ marginLeft: '4px', fontWeight: 'normal' }}>#{selectedOrderId}</span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant='body2' color='textSecondary' style={{ fontWeight: 'bold' }}>
                Status:
                <span style={{ marginLeft: '4px', fontWeight: 'normal' }}>{selectedOrder.status}</span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant='body2' color='textSecondary' style={{ fontWeight: 'bold' }}>
                Booth ID:
                <span style={{ marginLeft: '4px', fontWeight: 'normal' }}>
                  #{booth.id} (Seq: {booth.sequence})
                </span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant='body2' color='textSecondary' style={{ fontWeight: 'bold' }}>
                Service Level:
                <span style={{ marginLeft: '4px', fontWeight: 'normal' }}>
                  {selectedOrder.service_level || 'Standard'}
                </span>
              </Typography>
            </Grid>

            {/* Second row */}
            <Grid item xs={12} sm={6} md={6}>
              <Typography variant='body2' color='textSecondary' style={{ fontWeight: 'bold' }}>
                Event Name:
                <span style={{ marginLeft: '4px', fontWeight: 'normal' }}>{selectedOrder.event_name}</span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Typography variant='body2' color='textSecondary' style={{ fontWeight: 'bold' }}>
                Customer:
                <span style={{ marginLeft: '4px', fontWeight: 'normal' }}>
                  {selectedOrder.first_name} {selectedOrder.last_name}
                </span>
              </Typography>
            </Grid>

            {/* Third row */}
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant='body2' color='textSecondary' style={{ fontWeight: 'bold' }}>
                Ship Date:
                <span style={{ marginLeft: '4px', fontWeight: 'normal' }}>
                  {selectedOrder.ship_date ? moment(selectedOrder.ship_date).format('MM/DD/YYYY') : 'N/A'}
                </span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant='body2' color='textSecondary' style={{ fontWeight: 'bold' }}>
                Arrival Date:
                <span style={{ marginLeft: '4px', fontWeight: 'normal' }}>
                  {selectedOrder.arrival_date ? moment(selectedOrder.arrival_date).format('MM/DD/YYYY') : 'N/A'}
                </span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant='body2' color='textSecondary' style={{ fontWeight: 'bold' }}>
                Event Date:
                <span style={{ marginLeft: '4px', fontWeight: 'normal' }}>
                  {selectedOrder.event_date ? moment(selectedOrder.event_date).format('MM/DD/YYYY') : 'N/A'}
                </span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant='body2' color='textSecondary' style={{ fontWeight: 'bold' }}>
                Return Ship Date:
                <span style={{ marginLeft: '4px', fontWeight: 'normal' }}>
                  {selectedOrder.return_ship_date ? moment(selectedOrder.return_ship_date).format('MM/DD/YYYY') : 'N/A'}
                </span>
              </Typography>
            </Grid>

            {/* Fourth row */}
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant='body2' color='textSecondary' style={{ fontWeight: 'bold' }}>
                Address:
                <span style={{ marginLeft: '4px', fontWeight: 'normal' }}>
                  {selectedOrder.shipping_address1}, {selectedOrder.shipping_city}, {selectedOrder.shipping_state}{' '}
                  {selectedOrder.shipping_zip}
                </span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant='body2' color='textSecondary' style={{ fontWeight: 'bold' }}>
                Props:
                <span style={{ marginLeft: '4px', fontWeight: 'normal' }}>{selectedOrder.props || 'Default'}</span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant='body2' color='textSecondary' style={{ fontWeight: 'bold' }}>
                Print Count:
                <span style={{ marginLeft: '4px', fontWeight: 'normal' }}>{selectedOrder.print_count || '0'}</span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant='body2' color='textSecondary' style={{ fontWeight: 'bold' }}>
                Backdrop:
                <span style={{ marginLeft: '4px', fontWeight: 'normal' }}>
                  {selectedOrder.backdrop_color || 'Default'}
                </span>
              </Typography>
            </Grid>

            {/* Fifth row */}
            {selectedOrder.print_count > 0 && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant='body2' color='textSecondary' style={{ fontWeight: 'bold' }}>
                  Print Layout:
                  <span style={{ marginLeft: '4px', fontWeight: 'normal' }}>
                    {selectedOrder.print_layout || 'Standard'}
                  </span>
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant='body2' color='textSecondary' style={{ fontWeight: 'bold' }}>
                Custom Logo:
                <span style={{ marginLeft: '4px', fontWeight: 'normal' }}>
                  {selectedOrder.custom_logo ? 'Yes' : 'No'}
                </span>
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Typography variant='h5' className={classes.sectionTitle}>
        Outbound Checklist
      </Typography>
      <Paper className={classes.paper}>
        <Typography variant='body2' color='textSecondary' paragraph>
          Please complete each step below before finalizing the outbound process.
        </Typography>

        {/* CORE VERIFICATION */}
        <Typography variant='h6' gutterBottom style={{ marginTop: '16px' }}>
          Verification Steps
        </Typography>
        <Divider style={{ marginBottom: '16px' }} />

        <div className={classes.checklistItem}>
          <Checkbox
            checked={checklist.verifyOrder}
            onChange={handleChecklistChange}
            name='verifyOrder'
            color='primary'
            className={classes.checkboxRoot}
          />
          <Typography>
            <strong>Verify Order Details</strong> - Confirm the order details above match what you're preparing for
            shipment.
          </Typography>
        </div>

        {/* SHIPPING LABELS */}
        <Typography variant='h6' gutterBottom style={{ marginTop: '24px' }}>
          Shipping Labels
        </Typography>
        <Divider style={{ marginBottom: '16px' }} />

        <div className={classes.checklistItem}>
          <Checkbox
            checked={checklist.printShippingLabels}
            onChange={handleChecklistChange}
            name='printShippingLabels'
            color='primary'
            className={classes.checkboxRoot}
          />
          <Typography style={{ display: 'flex', alignItems: 'center' }}>
            <strong>Print Shipping Labels</strong> - Print all shipping labels needed for this order.
            <Button
              variant='contained'
              color='primary'
              size='small'
              startIcon={<Icon>print</Icon>}
              style={{ marginLeft: '16px' }}
              onClick={() => {
                // Create a window.print() trigger for the shipping labels
                const printWindow = window.open('', '_blank');
                if (!printWindow) {
                  ctx.handleNotifications(
                    true,
                    'error',
                    'Unable to open print window. Please check your popup blocker settings.'
                  );
                  return;
                }
                printWindow.document.write('<html><head><title>Print Shipping Labels</title>');
                printWindow.document.write('</head><body>');

                // Add the outbound label
                printWindow.document.write('<div style="margin-bottom: 20px;">');
                printWindow.document.write(
                  `<img src="https://s3.us-east-1.amazonaws.com/labels.boothbymail.com/${
                    process.env.REACT_APP_SITE_ENV
                  }/${selectedOrderId}/out?u=${new Date().getTime()}" style="width: 100%; max-width: 800px;" />`
                );
                printWindow.document.write('</div>');

                // Add the inbound label
                printWindow.document.write('<div style="margin-bottom: 20px;">');
                printWindow.document.write(
                  `<img src="https://s3.us-east-1.amazonaws.com/labels.boothbymail.com/${
                    process.env.REACT_APP_SITE_ENV
                  }/${selectedOrderId}/in?u=${new Date().getTime()}" style="width: 100%; max-width: 800px;" />`
                );
                printWindow.document.write('</div>');

                // Add the extra label if needed
                if (selectedOrder && selectedOrder.print_count >= 300) {
                  printWindow.document.write('<div style="margin-bottom: 20px;">');
                  printWindow.document.write(
                    `<img src="https://s3.us-east-1.amazonaws.com/labels.boothbymail.com/${
                      process.env.REACT_APP_SITE_ENV
                    }/${selectedOrderId}/extra?u=${new Date().getTime()}" style="width: 100%; max-width: 800px;" />`
                  );
                  printWindow.document.write('</div>');
                }

                printWindow.document.write('</body></html>');
                printWindow.document.close();
                printWindow.focus();
                printWindow.print();
                // Mark the checklist item as complete after printing
                setChecklist({ ...checklist, printShippingLabels: true });
              }}
            >
              Print Shipping Labels
            </Button>
          </Typography>
        </div>

        <div className={classes.checklistItem}>
          <Checkbox
            checked={checklist.tapeZipTiesToReturnLabel}
            onChange={handleChecklistChange}
            name='tapeZipTiesToReturnLabel'
            color='primary'
            className={classes.checkboxRoot}
          />
          <Typography>
            <strong>Tape Zip Ties to Return Label</strong> - Attach 2 long zip ties to the RETURN shipping label. (the
            return label has BoothByMail as the SHIP TO address)
          </Typography>
        </div>

        {/* EQUIPMENT */}
        <Typography variant='h6' gutterBottom style={{ marginTop: '24px' }}>
          Equipment Preparation
        </Typography>
        <Divider style={{ marginBottom: '16px' }} />

        <div className={classes.checklistItem}>
          <Checkbox
            checked={checklist.includeBackdropTripods}
            onChange={handleChecklistChange}
            name='includeBackdropTripods'
            color='primary'
            className={classes.checkboxRoot}
          />
          <Typography>
            <strong>Include Backdrop Tripods</strong> - Make sure 2 backdrop tripods are included in the case.
          </Typography>
        </div>

        <div className={classes.checklistItem}>
          <Checkbox
            checked={checklist.includeBackdropCrossbar}
            onChange={handleChecklistChange}
            name='includeBackdropCrossbar'
            color='primary'
            className={classes.checkboxRoot}
          />
          <Typography>
            <strong>Include Backdrop Crossbar</strong> - Make sure 1 backdrop crossbar is included in the case.
          </Typography>
        </div>

        <div className={classes.checklistItem}>
          <Checkbox
            checked={checklist.verifyIpadFullyCharged}
            onChange={handleChecklistChange}
            name='verifyIpadFullyCharged'
            color='primary'
            className={classes.checkboxRoot}
          />
          <Typography>
            <strong>Verify iPad Charge</strong> - Ensure iPad is charged to 100% battery level.
          </Typography>
        </div>

        <div className={classes.checklistItem}>
          <Checkbox
            checked={checklist.includeChargingCables}
            onChange={handleChecklistChange}
            name='includeChargingCables'
            color='primary'
            className={classes.checkboxRoot}
          />
          <Typography>
            <strong>Include Charging Cables</strong> - Include charging cables for iPad and printer.
          </Typography>
        </div>

        {/* Backdrop - show with color-specific verbiage */}
        <div className={classes.checklistItem}>
          <Checkbox
            checked={checklist.includeCorrectBackdrop}
            onChange={handleChecklistChange}
            name='includeCorrectBackdrop'
            color='primary'
            className={classes.checkboxRoot}
          />
          <Typography>
            <strong>Include Backdrop</strong> - Add the{' '}
            {selectedOrder && selectedOrder.backdrop_color && selectedOrder.backdrop_color !== 'Default'
              ? selectedOrder.backdrop_color
              : 'default'}{' '}
            backdrop to the shipment.
          </Typography>
        </div>

        {/* Props - dynamic text based on order */}
        <div className={classes.checklistItem}>
          <Checkbox
            checked={checklist.insertReturnLabel}
            onChange={handleChecklistChange}
            name='insertReturnLabel'
            color='primary'
            className={classes.checkboxRoot}
          />
          <Typography>
            <strong>Insert Return Label</strong> - Insert RETURN shipping label with attached zip ties and props in
            space for iPad.
          </Typography>
        </div>

        <div className={classes.checklistItem}>
          <Checkbox
            checked={checklist.verifyProps}
            onChange={handleChecklistChange}
            name='verifyProps'
            color='primary'
            className={classes.checkboxRoot}
          />
          <Typography>
            <strong>Include Props</strong> - Add the{' '}
            {selectedOrder && selectedOrder.props && selectedOrder.props !== 'Default'
              ? selectedOrder.props
              : 'default general'}{' '}
            props to the shipment.
          </Typography>
        </div>

        {/* IPAD SETUP */}
        <Typography variant='h6' gutterBottom style={{ marginTop: '24px' }}>
          iPad Setup
        </Typography>
        <Divider style={{ marginBottom: '16px' }} />

        <div className={classes.checklistItem}>
          <Checkbox
            checked={checklist.unplugAndTuckWhiteCord}
            onChange={handleChecklistChange}
            name='unplugAndTuckWhiteCord'
            color='primary'
            className={classes.checkboxRoot}
          />
          <Typography>
            <strong>Prepare iPad Cords</strong> - Unplug iPad and tuck white cord in foam, plug black cord into iPad.
          </Typography>
        </div>

        <div className={classes.checklistItem}>
          <Checkbox
            checked={checklist.turnOnIpadGoToAdmin}
            onChange={handleChecklistChange}
            name='turnOnIpadGoToAdmin'
            color='primary'
            className={classes.checkboxRoot}
          />
          <Typography>
            <strong>Access Admin Screen</strong> - Turn on iPad and go to admin screen.
          </Typography>
        </div>

        <div className={classes.checklistItem}>
          <Checkbox
            checked={checklist.pullBoothConfig}
            onChange={handleChecklistChange}
            name='pullBoothConfig'
            color='primary'
            className={classes.checkboxRoot}
          />
          <Typography>
            <strong>Pull Booth Config</strong> - Tap "pull booth config" button in admin screen.
          </Typography>
        </div>

        <div className={classes.checklistItem}>
          <Checkbox
            checked={checklist.verifyCorrectAlbum}
            onChange={handleChecklistChange}
            name='verifyCorrectAlbum'
            color='primary'
            className={classes.checkboxRoot}
          />
          <Typography>
            <strong>Verify Album Creation</strong> - Verify an album is being created for the correct name/event number.
          </Typography>
        </div>

        {/* Logo verification if custom logo */}
        {selectedOrder && selectedOrder.custom_logo && (
          <div className={classes.checklistItem}>
            <Checkbox
              checked={checklist.verifyCustomLogo}
              onChange={handleChecklistChange}
              name='verifyCustomLogo'
              color='primary'
              className={classes.checkboxRoot}
            />
            <Typography>
              <strong>Verify Custom Logo</strong> - Check LOGO to make sure it has been uploaded.
              {selectedOrder.logo_url && (
                <img
                  src={selectedOrder.logo_url}
                  alt='Custom Logo'
                  style={{
                    display: 'block',
                    maxWidth: '200px',
                    maxHeight: '80px',
                    marginTop: '8px',
                    border: '1px solid #ddd',
                    padding: '4px',
                  }}
                />
              )}
            </Typography>
          </div>
        )}

        {/* PRINT SETUP - Only for Print Booths */}
        {selectedOrder && selectedOrder.print_count === 0 ? (
          <div>
            <Typography variant='h6' gutterBottom style={{ marginTop: '24px' }}>
              Digital Booth Setup
            </Typography>
            <Divider style={{ marginBottom: '16px' }} />

            <div className={classes.checklistItem}>
              <Checkbox
                checked={checklist.disablePrintingForDigital}
                onChange={handleChecklistChange}
                name='disablePrintingForDigital'
                color='primary'
                className={classes.checkboxRoot}
              />
              <Typography>
                <strong>Disable Printing</strong> - Go to PRINT SETTINGS and turn OFF PRINTING.
              </Typography>
            </div>
          </div>
        ) : selectedOrder && selectedOrder.print_count > 0 ? (
          <div>
            <Typography variant='h6' gutterBottom style={{ marginTop: '24px' }}>
              Print Booth Setup
            </Typography>
            <Divider style={{ marginBottom: '16px' }} />

            <div className={classes.checklistItem}>
              <Checkbox
                checked={checklist.setupPrinterForPrinting}
                onChange={handleChecklistChange}
                name='setupPrinterForPrinting'
                color='primary'
                className={classes.checkboxRoot}
              />
              <Typography>
                <strong>Setup Printer</strong> - Pull out printer and turn it on.
              </Typography>
            </div>

            <div className={classes.checklistItem}>
              <Checkbox
                checked={checklist.addPaperAndInk}
                onChange={handleChecklistChange}
                name='addPaperAndInk'
                color='primary'
                className={classes.checkboxRoot}
              />
              <Typography>
                <strong>Add Paper and Ink</strong> - Add paper to tray & insert ink cartridge.
              </Typography>
            </div>

            {/* Print supplies based on print count */}
            {selectedOrder.print_count === 100 && (
              <div className={classes.checklistItem}>
                <Checkbox
                  checked={checklist.addExtraInkPaper100}
                  onChange={handleChecklistChange}
                  name='addExtraInkPaper100'
                  color='primary'
                  className={classes.checkboxRoot}
                />
                <Typography>
                  <strong>Add Extra Supplies (100 prints)</strong> - Add 2 ink cartridges and 5 paper packs to the
                  shipping case.
                </Typography>
              </div>
            )}

            {selectedOrder.print_count === 200 && (
              <div className={classes.checklistItem}>
                <Checkbox
                  checked={checklist.addExtraInkPaper200}
                  onChange={handleChecklistChange}
                  name='addExtraInkPaper200'
                  color='primary'
                  className={classes.checkboxRoot}
                />
                <Typography>
                  <strong>Add Extra Supplies (200 prints)</strong> - Add 5 ink cartridges and 10 paper packs to the
                  shipping case.
                </Typography>
              </div>
            )}

            {selectedOrder.print_count >= 300 && (
              <div className={classes.checklistItem}>
                <Checkbox
                  checked={checklist.prepareExtraShipment300}
                  onChange={handleChecklistChange}
                  name='prepareExtraShipment300'
                  color='primary'
                  className={classes.checkboxRoot}
                />
                <Typography>
                  <strong>Prepare Extra Shipment (300+ prints)</strong> - Make sure an extra shipment label is printed
                  and extra prints are put into a box and shipped along with the photo booth equipment.
                </Typography>
              </div>
            )}

            <div className={classes.checklistItem}>
              <Checkbox
                checked={checklist.connectToWifi}
                onChange={handleChecklistChange}
                name='connectToWifi'
                color='primary'
                className={classes.checkboxRoot}
              />
              <Typography>
                <strong>Connect to WiFi</strong> - Go to iPad general settings, go to WiFi and select printer WiFi to
                connect.
              </Typography>
            </div>

            <div className={classes.checklistItem}>
              <Checkbox
                checked={checklist.selectPrinter}
                onChange={handleChecklistChange}
                name='selectPrinter'
                color='primary'
                className={classes.checkboxRoot}
              />
              <Typography>
                <strong>Select Printer</strong> - In the booth software, select the printer.
              </Typography>
            </div>

            <div className={classes.checklistItem}>
              <Checkbox
                checked={checklist.performTestPrint}
                onChange={handleChecklistChange}
                name='performTestPrint'
                color='primary'
                className={classes.checkboxRoot}
              />
              <Typography>
                <strong>Test Print</strong> - Perform a test print to verify printer is working properly.
              </Typography>
            </div>

            <div className={classes.checklistItem}>
              <Checkbox
                checked={checklist.packPrinter}
                onChange={handleChecklistChange}
                name='packPrinter'
                color='primary'
                className={classes.checkboxRoot}
              />
              <Typography>
                <strong>Pack Printer</strong> - Turn off printer, unplug and place in case. Paper tray goes over power
                cords and test print on top of paper tray.
              </Typography>
            </div>
          </div>
        ) : null}

        {/* FINAL STEPS */}
        <Typography variant='h6' gutterBottom style={{ marginTop: '24px' }}>
          Final Steps
        </Typography>
        <Divider style={{ marginBottom: '16px' }} />

        <div className={classes.checklistItem}>
          <Checkbox
            checked={checklist.exitAdminMode}
            onChange={handleChecklistChange}
            name='exitAdminMode'
            color='primary'
            className={classes.checkboxRoot}
          />
          <Typography>
            <strong>Exit Admin Mode</strong> - Exit admin mode and select LETS GO, turn off iPad screen.
          </Typography>
        </div>

        <div className={classes.checklistItem}>
          <Checkbox
            checked={checklist.placeIpadInCase}
            onChange={handleChecklistChange}
            name='placeIpadInCase'
            color='primary'
            className={classes.checkboxRoot}
          />
          <Typography>
            <strong>Place iPad in Case</strong> - Place iPad assembly in shipping case.
          </Typography>
        </div>

        <div className={classes.checklistItem}>
          <Checkbox
            checked={checklist.insertInstructionSheet}
            onChange={handleChecklistChange}
            name='insertInstructionSheet'
            color='primary'
            className={classes.checkboxRoot}
          />
          <Typography>
            <strong>Insert Instructions</strong> - Insert customer instruction sheet on top of iPad.
          </Typography>
        </div>

        <div className={classes.checklistItem}>
          <Checkbox
            checked={checklist.closeAndSecureCase}
            onChange={handleChecklistChange}
            name='closeAndSecureCase'
            color='primary'
            className={classes.checkboxRoot}
          />
          <Typography>
            <strong>Secure Case</strong> - Close shipping case, stick shipping label on left side of case, add no less
            than 2 zip ties to ensure booth stays locked until it reaches the customer.
          </Typography>
        </div>
      </Paper>

      <div className={classes.buttonContainer}>
        <Button variant='outlined' color='default' onClick={handlePauseOutbound} className={classes.cancelButton}>
          Pause Outbound
        </Button>

        <Button
          variant='outlined'
          color='secondary'
          onClick={handleUndoOutbound}
          disabled={undoLoading}
          style={{ marginRight: 'auto' }}
        >
          {undoLoading ? (
            <>
              <CircularProgress size={24} style={{ marginRight: '8px', color: 'secondary' }} />
              Resetting...
            </>
          ) : (
            'Undo Outbound'
          )}
        </Button>

        <Button
          variant='contained'
          className={classes.finishButton}
          disabled={!allTasksCompleted || completeLoading}
          onClick={handleFinishOutbound}
        >
          {completeLoading ? (
            <>
              <CircularProgress size={24} style={{ marginRight: '8px', color: 'white' }} />
              Processing...
            </>
          ) : (
            'Complete Outbound'
          )}
        </Button>
      </div>
    </div>
  );
}

export default BoothOutbound;
